import { useNavigate } from "react-router"
import React, { forwardRef, Suspense, useRef } from "react"
import { FormikProps } from "formik"
import { ClosingTimeType, CreateCarResource, CreateCarResourceForm } from "../../resourcesTypes"
import { useApiMutation } from "../../../../../../../core/api/useApiMutation"
import { editResource, getCarResource, getResource, ServiceCenterResourcesQuery } from "../../resourcesApi"
import { enqueueSnackbar } from "notistack"
import {
    SERVICE_CENTER_ADMIN_ORGANIZATION_RESOURCE_ROUTE,
    SERVICE_CENTER_ADMIN_ORGANIZATION_ROUTE,
    SERVICE_CENTER_ADMIN_ROUTE,
} from "../../../../../../../constants/routes"
import { LocalizedStrict } from "../../../../../../localization/components/AppLocalized"
import { PageHeader } from "../../../../../../../shared/components/pageHeader/PageHeader"
import { Page } from "../../../../../../../shared/components/pageHeader/PageHeaderApi"
import { Button } from "../../../../../../office/admin/components/Button"
import {
    Breadcrumb,
    BreadcrumbLink,
    StandardBreadcrumbs,
} from "../../../../../../../shared/components/links/StandardBreadcrumbs"
import { useParams } from "react-router-dom"
import { useApiQuery } from "../../../../../../../core/api/useApiQuery"
import { SupportEntityQuery } from "../../../../../../support/admin/entities/supportEntityApi"
import { LoadingSpinner } from "../../../../../../../shared/components/loading/LoadingSpinner"
import { QueryErrorBoundary } from "../../../../../../../core/api/components/QueryErrorBoundary"
import { CreateUpdateCarForm } from "../../components/car/CreateUpdateCarForm"
import { DateTimeFactory, useDateTime } from "../../../../../../localization/useDateTime"
import { transformCarFormToResource } from "./utils"

export function EditCar() {
    const carFormRef = useRef<FormikProps<CreateCarResourceForm>>(null)

    const handleSaveClick = () => {
        carFormRef.current?.submitForm()
    }

    return (
        <>
            <LocalizedStrict id="service-center-admin-organization-resources-update-car-title" attrs={{ title: true }}>
                <PageHeader
                    page={Page.SERVICE_CENTER_ADMIN_ORGANIZATION_RESOURCE_EDIT_CAR_ROUTE}
                    title="Edit car"
                    rightElement={
                        <LocalizedStrict id="support-create-company-update-button">
                            <Button variant="contained" onClick={handleSaveClick}>
                                Update
                            </Button>
                        </LocalizedStrict>
                    }
                >
                    <StandardBreadcrumbs>
                        <LocalizedStrict id="merchant-nav-item-home">
                            <BreadcrumbLink to={SERVICE_CENTER_ADMIN_ROUTE}>Home</BreadcrumbLink>
                        </LocalizedStrict>
                        <LocalizedStrict id="service-center-admin-nav-item-organization">
                            <BreadcrumbLink to={SERVICE_CENTER_ADMIN_ORGANIZATION_ROUTE}>Organization</BreadcrumbLink>
                        </LocalizedStrict>
                        <LocalizedStrict id="service-center-admin-nav-item-organization-resource-update-car">
                            <Breadcrumb>Update car</Breadcrumb>
                        </LocalizedStrict>
                    </StandardBreadcrumbs>
                </PageHeader>
            </LocalizedStrict>
            <QueryErrorBoundary>
                <Suspense fallback={<LoadingSpinner />}>
                    <Car ref={carFormRef} />
                </Suspense>
            </QueryErrorBoundary>
        </>
    )
}

const transformData = (data: CreateCarResource, dateTimeFactory: DateTimeFactory): CreateCarResourceForm => {
    const { bookingTime, closingTimes, ...rest } = data
    const closingTime = closingTimes.filter((i) => i.type === ClosingTimeType.DAILY)?.[0]?.periodInTime

    return {
        ...rest,
        closingTimes,
        bookingTimeType: bookingTime ? "acceptBookingTime" : "blockBookingTime",
        ...(bookingTime && {
            bookingTime: {
                periodInTime: {
                    from: dateTimeFactory.fromHourMinute(bookingTime?.periodInTime.from),
                    to: dateTimeFactory.fromHourMinute(bookingTime?.periodInTime?.to),
                },
            },
        }),
        ...(closingTime && {
            closingTimesDaily: {
                periodInTime: {
                    from: dateTimeFactory.fromHourMinute(closingTime.from),
                    to: dateTimeFactory.fromHourMinute(closingTime.to),
                },
            },
        }),
    }
}

const Car = forwardRef<FormikProps<CreateCarResourceForm>>((_props, ref) => {
    const navigate = useNavigate()
    const { carId } = useParams()
    const carIdNumber = Number(carId)
    const dateTimeFactory = useDateTime()

    const carDetails = useApiQuery(getCarResource, {
        queryName: SupportEntityQuery.DETAILS,
        dependencies: {
            carId: carIdNumber ?? 0,
        },
        suspense: true,
        useErrorBoundary: true,
        enabled: Boolean(carIdNumber),
        select: (data) => transformData(data, dateTimeFactory),
    })

    const { mutate } = useApiMutation(editResource, {
        async onSuccess(_, data, context) {
            navigate(SERVICE_CENTER_ADMIN_ORGANIZATION_RESOURCE_ROUTE)
            await context.queryClient.invalidateQueries({
                queryKey: [ServiceCenterResourcesQuery.SERVICE_CENTER_RESOURCES],
            })
            if (data?.serviceResponse?.displayMessage) {
                enqueueSnackbar({
                    variant: "success",
                    message: data?.serviceResponse?.displayMessage,
                })
            }
        },
    })

    const editCar = (value: CreateCarResourceForm) => {
        if (!carIdNumber) throw new Error("Car ID is undefined")

        const updatedForm = transformCarFormToResource(value, dateTimeFactory)

        mutate({
            roomId: carIdNumber,
            ...updatedForm,
        })
    }

    return <CreateUpdateCarForm initialValues={carDetails.data} ref={ref} onSubmit={editCar} />
})
